if (!customElements.get('td-sticky-atc')) {
  const SELECTORS = {
    bundleSiblingLink: '[data-td-bundle-sibling-link]',
    stickyAtc: 'td-sticky-atc',
  };

  class TdStickyAtc extends HTMLElement {
    constructor() {
      super();

      this.ACTIVE_CLASS = 'td-active';
      this.handleScroll = this.handleScroll.bind(this);
      this.handleAtcClick = this.handleAtcClick.bind(this);
      this.handlePriceChanged = this.handlePriceChanged.bind(this);
      this.handleVariantChanged = this.handleVariantChanged.bind(this);
      this.handleSectionContentChanged = this.handleSectionContentChanged.bind(this);
      this.handleSellingPlanInputChange = this.handleSellingPlanInputChange.bind(this);
      this.handleSellingPlanSelectChange = this.handleSellingPlanSelectChange.bind(this);
      this.setSelectedBundleSibling = this.setSelectedBundleSibling.bind(this);
      this.restoreScrollPosition = this.restoreScrollPosition.bind(this);
      this.isSwitchingBundleSibling = false;
    }

    connectedCallback() {
      this.ACTIVE_CLASS = 'td-active';
      this.OPTION_SELECTS = this.querySelectorAll('[data-sticky-atc-option-selector]');
      this.BUNDLE_SIBLING_SELECT = this.querySelector('[data-sticky-atc-bundle-sibling-select]');
      this.PRODUCT_FORM_OPTIONS = this.querySelectorAll('product-variants .product-variant__input');
      this.cacheProductBindings();
      
      if (!this.ATC_BUTTON || !this.ATC_PRICE || !this.FORM_ATC_BUTTON || !this.OBSERVABLE_TARGET) {
        return
      }
      this.attachListeners();
      if (this.SELLING_PLAN_SELECT && this.SKIO_COMPONENT && this.SELLING_PLAN_INPUT) {
        this.SKIO_SHADOW_ROOT = this.SKIO_COMPONENT.shadowRoot;
        if (this.SKIO_SHADOW_ROOT) {
          this.reflectSellingPlan();
          this.attachSkioRelatedListeners();
        }
      }
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("td:price-changed", this.handlePriceChanged);
      window.removeEventListener("td:variant-changed", this.handleVariantChanged);
      document.removeEventListener('section-content::changed', this.handleSectionContentChanged);

      if (this.ATC_BUTTON) {
        this.ATC_BUTTON.removeEventListener("click", this.handleAtcClick);
      }
      if (this.BUNDLE_SIBLING_SELECT) {
        this.BUNDLE_SIBLING_SELECT.removeEventListener("change", this.setSelectedBundleSibling);
      }
      this.detachSkioRelatedListeners();
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    cacheProductBindings() {
      this.ATC_BUTTON = this.querySelector('[data-sticky-atc-button]');
      this.ATC_PRICE = this.querySelector('.td-sticky-atc__price');
      this.FORM_ATC_BUTTON = document.querySelector('.product__add-to-cart');
      this.OBSERVABLE_TARGET = document.querySelector('[data-sticky-atc-observable]');
      this.SELLING_PLAN_SELECT = this.querySelector('[data-sticky-atc-plan-group]');
      this.SELLING_PLAN_INPUT = document.querySelector('[data-selling-plan-input]');
      this.SKIO_COMPONENT = document.querySelector('skio-plan-picker');
    }

    attachListeners() {
      this.isTargetVisible = true;
      this.setupIntersectionObserver();

      window.addEventListener("scroll", this.handleScroll, { passive: true });
      document.addEventListener('section-content::changed', this.handleSectionContentChanged);

      this.ATC_BUTTON.addEventListener("click", this.handleAtcClick);
      window.addEventListener("td:price-changed", this.handlePriceChanged);
      window.addEventListener("td:variant-changed", this.handleVariantChanged);
      this.OPTION_SELECTS.forEach(select => {
        select.addEventListener("change", (event) => {
          this.setSelectedOptions(event);
        });
      });

      if (this.BUNDLE_SIBLING_SELECT) {
        this.BUNDLE_SIBLING_SELECT.addEventListener("change", this.setSelectedBundleSibling);
      }
      
    }

    handleScroll() {
      this.updateStickyState();
    }

    handleAtcClick() {
      this.cacheProductBindings();
      this.FORM_ATC_BUTTON?.click();
    }

    handlePriceChanged(event) {
      this.updateAtcPrice(event);
    }

    handleVariantChanged(event) {
      this.reflectSelectedOptions(event);
    }

    handleSectionContentChanged() {
      const previousTarget = this.OBSERVABLE_TARGET;
      const previousSellingPlanInput = this.SELLING_PLAN_INPUT;
      const previousSellingPlanSelect = this.SELLING_PLAN_SELECT;
      this.cacheProductBindings();

      if (!this.observer) {
        return;
      }

      if (previousTarget && previousTarget !== this.OBSERVABLE_TARGET) {
        this.observer.unobserve(previousTarget);
      }

      if (this.OBSERVABLE_TARGET) {
        this.observer.observe(this.OBSERVABLE_TARGET);
      }

      if (
        previousSellingPlanInput !== this.SELLING_PLAN_INPUT ||
        previousSellingPlanSelect !== this.SELLING_PLAN_SELECT
      ) {
        if (this.SELLING_PLAN_SELECT && this.SKIO_COMPONENT && this.SELLING_PLAN_INPUT) {
          this.SKIO_SHADOW_ROOT = this.SKIO_COMPONENT.shadowRoot;
          if (this.SKIO_SHADOW_ROOT) {
            this.reflectSellingPlan();
            this.attachSkioRelatedListeners();
          }
        } else {
          this.detachSkioRelatedListeners();
        }
      }
    }

    attachSkioRelatedListeners() {
      this.detachSkioRelatedListeners();
      this.SELLING_PLAN_INPUT.addEventListener("change", this.handleSellingPlanInputChange);
      this.SELLING_PLAN_SELECT.addEventListener("change", this.handleSellingPlanSelectChange);
    }

    detachSkioRelatedListeners() {
      this.SELLING_PLAN_INPUT?.removeEventListener("change", this.handleSellingPlanInputChange);
      this.SELLING_PLAN_SELECT?.removeEventListener("change", this.handleSellingPlanSelectChange);
    }

    handleSellingPlanInputChange() {
      this.reflectSellingPlan();
    }

    handleSellingPlanSelectChange() {
      this.setSellingPlan();
    }

    setupIntersectionObserver() {
      this.hasScrolledPastTarget = false;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.hasScrolledPastTarget = false;
          } else if (entry.boundingClientRect.bottom < 0) {
            this.hasScrolledPastTarget = true;
          }
          this.updateStickyState();
        });
      });
      this.observer.observe(this.OBSERVABLE_TARGET);
    }

    updateStickyState() {
      const targetRect = this.OBSERVABLE_TARGET.getBoundingClientRect();
      const isPastTarget = targetRect.bottom < 0;
      const shouldShow = isPastTarget && !this.isCloseToBottomOfDocument();

      if (shouldShow) {
        this.classList.add(this.ACTIVE_CLASS);
        document.body.classList.add('td-sticky-atc-active');
      } else {
        this.classList.remove(this.ACTIVE_CLASS);
        document.body.classList.remove('td-sticky-atc-active');
      }
    }

    isCloseToBottomOfDocument(offset = 250) {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      return documentHeight - (scrollPosition + windowHeight) <= offset;
    }

    setSellingPlan() {
      const selectedOption = this.SELLING_PLAN_SELECT.options[this.SELLING_PLAN_SELECT.selectedIndex];
      const sellingPlanId = selectedOption.dataset.stickyAtcPlan;
      if (sellingPlanId === 'one-time-purchase') {
        const sellingPlanGroupRadio = this.SKIO_SHADOW_ROOT.querySelector(`#${this.SELLING_PLAN_SELECT.dataset.stickyAtcOneTime}`);
        if (!sellingPlanGroupRadio) {return}
        sellingPlanGroupRadio.checked = true;
        sellingPlanGroupRadio.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        const sellingPlanGroupRadio = this.SKIO_SHADOW_ROOT.querySelector(`#${this.SELLING_PLAN_SELECT.dataset.stickyAtcPlanGroup}`);
        const sellingPlanRadio = this.SKIO_SHADOW_ROOT.querySelector(`#selling_plan_button-${sellingPlanId}`);
        if (!sellingPlanGroupRadio || !sellingPlanRadio) {return}
        sellingPlanRadio.checked = true;
        sellingPlanGroupRadio.checked = true;
        sellingPlanRadio.dispatchEvent(new Event('change', { bubbles: true }));
        sellingPlanGroupRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    reflectSellingPlan() {
      if (this.SELLING_PLAN_INPUT.value.trim() === '') {
        this.SELLING_PLAN_SELECT.value = 'one-time-purchase';
      } else {
        this.SELLING_PLAN_SELECT.value = this.SELLING_PLAN_INPUT.value;
      }
    }

    updateAtcPrice(event) {
      const price = event?.detail?.price;
      if (price) {
        this.ATC_PRICE.innerHTML = price;
      }
    }

    setSelectedOptions(event) {
      const optionToSelect = document.querySelector(`.product-variant__item input[value="${event?.target?.value}"]`);
      if (optionToSelect) {
        optionToSelect.checked = true;
        optionToSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      //Handle custom option selectors
      document.querySelectorAll(`.product_custom_var span[data-value]`).forEach(option => {
        option.classList.remove('active');
      });
      const customOptionToSelect = document.querySelector(`.product_custom_var span[data-value="${event?.target?.value}"]`);
      if (customOptionToSelect) {
        customOptionToSelect.classList.add('active');
      }
    }

    reflectSelectedOptions(event) {
      const selectedVariant = event?.detail?.selected_variant;
      if (!selectedVariant) {
        return;
      }
      selectedVariant.options.map((option, index) => {
        const optionSelect = this.OPTION_SELECTS[index];
        if (optionSelect) {
          optionSelect.value = option;
        }
      })
    }

    setSelectedBundleSibling(event) {
      const productUrl = event?.target?.value;
      const productHandle = event?.target?.selectedOptions?.[0]?.dataset?.stickyAtcBundleProductHandle || '';
      const nextUrl = productUrl ? new URL(productUrl, window.location.origin) : null;

      if (!nextUrl || nextUrl.pathname === window.location.pathname) {
        return;
      }

      if (this.isSwitchingBundleSibling) {
        return;
      }

      const bundleSiblingLink = this.getBundleSiblingLink(productHandle, nextUrl.toString());

      if (!bundleSiblingLink) {
        window.location.href = nextUrl.toString();
        return;
      }

      this.isSwitchingBundleSibling = true;
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      const restoreScroll = () => this.restoreScrollPosition(scrollX, scrollY);

      this.replaceStickyAtc(nextUrl.toString())
        .catch((error) => {
          console.error(error);
          window.location.href = nextUrl.toString();
        })
        .finally(() => {
          restoreScroll();
          this.isSwitchingBundleSibling = false;
        });

      bundleSiblingLink.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        })
      );

      window.setTimeout(restoreScroll, 80);
      window.setTimeout(restoreScroll, 180);
    }

    getBundleSiblingLink(productHandle, productUrl) {
      if (productHandle && typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        const matchingHandleLink = document.querySelector(
          `${SELECTORS.bundleSiblingLink}[data-td-product-handle="${CSS.escape(productHandle)}"]`
        );

        if (matchingHandleLink) {
          return matchingHandleLink;
        }
      }

      return Array.from(document.querySelectorAll(SELECTORS.bundleSiblingLink)).find((link) => {
        return link.dataset.tdProductUrl === productUrl;
      });
    }

    async replaceStickyAtc(productUrl) {
      const response = await fetch(productUrl, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Failed to fetch sticky ATC markup for ${productUrl}`);
      }

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextStickyAtc = parsedDocument.querySelector(SELECTORS.stickyAtc);

      if (!nextStickyAtc) {
        throw new Error(`Sticky ATC markup missing for ${productUrl}`);
      }

      this.replaceWith(nextStickyAtc);

      return nextStickyAtc;
    }

    restoreScrollPosition(scrollX, scrollY) {
      window.scrollTo(scrollX, scrollY);
      window.requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
        window.requestAnimationFrame(() => {
          window.scrollTo(scrollX, scrollY);
        });
      });
    }

  }

  customElements.define('td-sticky-atc', TdStickyAtc);
}
